// Fianza event / version model.
//
// A fianza (contracts/{id}.fianzas[]) carries a typed, append-only event log
// (`eventos[]`) plus a lifecycle state (`estadoFianza`) and a factura ledger
// (`facturas[]`). The root denormalized fields (montoAfianzado, monto, vigencia,
// tipoFianza, numeroFianza) are a live PROJECTION of the current valid version,
// so every existing consumer keeps reading them unchanged; history, terminal
// state, and the factura ledger are additive.
//
// Pure functions only — no React, no Firestore imports (the one `calculations`
// import is another pure util). Callers persist the returned objects.

import {
  getPrimaAmount,
  getFacturasVigentes,
  calculateTarifa,
  parseNumber,
  toTimestamp,
  FACTURA_MODES,
} from './calculations';

// Re-exported so the many existing `from './fianzaEventos'` import sites keep
// working; the canonical definitions live in calculations.js because the vigente
// fold is money math shared with getPrimaAmount.
export { FACTURA_MODES, getFacturasVigentes };

export const ESTADO_FIANZA = {
  ACTIVA: 'activa',
  CANCELADA: 'cancelada',
  ANULADA: 'anulada',
  SUSTITUIDA: 'sustituida',
};

export const EVENTO_TIPOS = {
  EMISION: 'emision',
  CAMBIO_TEXTO: 'cambio_texto',
  AUMENTO: 'aumento',
  DISMINUCION: 'disminucion',
  CAMBIO_VIGENCIA: 'cambio_vigencia',
  AUMENTO_DISMINUCION_VIGENCIA: 'aumento_disminucion_vigencia',
  CAMBIO_DATOS: 'cambio_datos',
  CAMBIO_FIADO_BENEFICIARIO: 'cambio_fiado_beneficiario',
  CANCELACION: 'cancelacion',
  ANULACION: 'anulacion',
  SUSTITUCION: 'sustitucion',
  // NON-terminal: the old fianza ran its full term and keeps estado + comisión;
  // the event links it to the new fianza entity that renews it (renovadaPor).
  RENOVACION: 'renovacion',
};

// Factura ledger modes are defined in calculations.js and re-exported above.
// The cliente pays per factura, so `pagada` lives on each entry (not the fianza).
//   - emision:   the original factura (facturas[0]).
//   - adicional: a new factura for a difference (aumento / extensión billed as
//     the diff); the prior factura stays valid → both counted.
//   - sustituye: fully replaces the prior valid factura from its fecha onward
//     (this is how a disminución works — cancel + reissue lower, NOT an additive
//     negative-prima factura).
export const FACTURA_MODE_LABELS = {
  emision: 'Emisión',
  adicional: 'Adicional',
  sustituye: 'Sustituye',
};

// An endoso has TWO independent axes, and collapsing them is a money bug:
//
//   1. tipos[]     — WHAT changed. Descriptive, and genuinely plural: a single
//                    endoso is often an aumento AND a cambio de texto.
//   2. efectoPrima — WHAT THE MONEY DID. This, and only this, drives the ledger.
//
// They do not imply each other. A vigencia extension on a multi-year fianza
// leaves montoAfianzado untouched yet bills an extra factura that complements the
// original coverage; a "cambio de texto" the user typed can still arrive with a
// factura attached. Keying the ledger off tipo missed both.
export const EFECTO_PRIMA = {
  // Nothing was billed. The endoso only restates the fianza.
  NINGUNO: 'ninguno',
  // A new factura for the DIFFERENCE. The prior one stays valid, so the primas
  // accrue: aumento de monto, ampliación de vigencia.
  ADICIONAL: 'adicional',
  // The afianzadora cancelled and reissued. The new factura replaces the prior
  // valid set: disminución de monto, reducción de vigencia.
  SUSTITUYE: 'sustituye',
};

export const EFECTO_PRIMA_LABELS = {
  ninguno: 'Sin cambio en la prima',
  adicional: 'Factura adicional (complementa la cobertura)',
  sustituye: 'Factura que sustituye (cancelan y reexpiden)',
};

// The seven user-selectable endoso types (excludes emisión + terminal events).
export const ENDOSO_TIPOS = [
  EVENTO_TIPOS.CAMBIO_TEXTO,
  EVENTO_TIPOS.AUMENTO,
  EVENTO_TIPOS.DISMINUCION,
  EVENTO_TIPOS.CAMBIO_VIGENCIA,
  EVENTO_TIPOS.AUMENTO_DISMINUCION_VIGENCIA,
  EVENTO_TIPOS.CAMBIO_DATOS,
  EVENTO_TIPOS.CAMBIO_FIADO_BENEFICIARIO,
];

// Terminal event type -> resulting estadoFianza. A sustitución ends the OLD
// fianza (→ sustituida) and is emitted alongside a brand-new fianza entity.
export const TERMINAL_TIPOS = {
  [EVENTO_TIPOS.CANCELACION]: ESTADO_FIANZA.CANCELADA,
  [EVENTO_TIPOS.ANULACION]: ESTADO_FIANZA.ANULADA,
  [EVENTO_TIPOS.SUSTITUCION]: ESTADO_FIANZA.SUSTITUIDA,
};

// Contract-level attached document types (contracts/{id}.documentos[]).
// NOTE: the carta de crédito a favor del fiado is deliberately NOT modelled —
// it is issued by the afianzadora to the fiado and never reaches the agente, so
// Surelia has no business holding it. The credit it represents IS tracked, as
// `saldoAFavor` on the factura ledger.
export const DOCUMENTO_TIPOS = {
  ACTA_ENTREGA: 'acta_entrega',
  CONVENIO_MODIFICATORIO: 'convenio_modificatorio',
  ANULACION: 'anulacion',
  OTRO: 'otro',
};

// Human-readable (es-MX) labels.
export const EVENTO_TIPO_LABELS = {
  emision: 'Emisión',
  cambio_texto: 'Cambio de texto',
  aumento: 'Aumento',
  disminucion: 'Disminución',
  cambio_vigencia: 'Cambio de vigencia',
  aumento_disminucion_vigencia: 'Aumento/disminución de vigencia',
  cambio_datos: 'Cambio de datos',
  cambio_fiado_beneficiario: 'Cambio de fiado o beneficiario',
  cancelacion: 'Cancelación',
  anulacion: 'Anulación',
  sustitucion: 'Sustitución',
  renovacion: 'Renovación',
};

export const DOCUMENTO_TIPO_LABELS = {
  acta_entrega: 'Acta de entrega',
  convenio_modificatorio: 'Convenio modificatorio',
  anulacion: 'Anulación',
  otro: 'Otro',
};

// ── Endosos pendientes ──────────────────────────────────────────────────────
// An endoso the agente has been ASKED for but has not applied yet. The eventos[]
// log is strictly retrospective — it records what already happened — so a
// requested endoso needs its own slot. It lives on the fianza (not the contract)
// because resolving the request and applying the real endoso must be one atomic
// write, and they only share an object at this level.
//
// Deliberately NOT following fianzasRequeridas' shape: no {value:} envelope
// (that exists because the AI extracts that field from the contract PDF), stable
// ids instead of positional ones, and ONE status enum defined here rather than
// string literals scattered across components.
export const ENDOSO_REQ_ESTATUS = {
  SOLICITADO: 'solicitado',
  EN_PROCESO: 'en_proceso',
  APLICADO: 'aplicado',
  CANCELADO: 'cancelado',
};

export const ENDOSO_REQ_ESTATUS_LABELS = {
  solicitado: 'Solicitado',
  en_proceso: 'En proceso',
  aplicado: 'Aplicado',
  cancelado: 'Cancelado',
};

// Who asked for it. Free-form enough to be useful in a report.
export const ENDOSO_SOLICITANTES = ['cliente', 'beneficiario', 'afianzadora', 'otro'];
export const ENDOSO_SOLICITANTE_LABELS = {
  cliente: 'Cliente',
  beneficiario: 'Beneficiario',
  afianzadora: 'Afianzadora',
  otro: 'Otro',
};

// Still awaiting action.
const ABIERTOS = [ENDOSO_REQ_ESTATUS.SOLICITADO, ENDOSO_REQ_ESTATUS.EN_PROCESO];

export function buildEndosoPendiente({
  tipos = [],
  descripcion = '',
  solicitadoPor = 'cliente',
  fechaLimite = null,
  createdBy = null,
  ts = null,
}) {
  const now = ts || Date.now();
  return {
    id: `endreq_${now}`,
    tipos,
    descripcion: descripcion.trim() || null,
    estatus: ENDOSO_REQ_ESTATUS.SOLICITADO,
    solicitadoPor,
    fechaSolicitud: now,
    fechaLimite: fechaLimite || null,
    createdBy,
    createdAt: now,
    // Filled in when the real endoso lands.
    eventoId: null,
    resueltoAt: null,
    canceladoMotivo: null,
  };
}

export function getEndososPendientes(fianza) {
  return Array.isArray(fianza?.endososPendientes) ? fianza.endososPendientes : [];
}

/** Only the ones still awaiting action. */
export function getEndososAbiertos(fianza) {
  return getEndososPendientes(fianza).filter((e) => ABIERTOS.includes(e?.estatus));
}

/**
 * Close a request because the endoso was actually applied. Returns a NEW fianza.
 * Called from inside the same write that appends the evento, so a request can
 * never be left open against an endoso that already happened.
 */
export function resolverEndosoPendiente(fianza, endosoRequeridoId, eventoId, ts = null) {
  if (!endosoRequeridoId) return fianza;
  const pendientes = getEndososPendientes(fianza);
  if (!pendientes.length) return fianza;
  const now = ts || Date.now();
  return {
    ...fianza,
    endososPendientes: pendientes.map((e) =>
      e.id === endosoRequeridoId
        ? { ...e, estatus: ENDOSO_REQ_ESTATUS.APLICADO, eventoId, resueltoAt: now }
        : e
    ),
  };
}

/**
 * Urgency of a pending endoso, from its due date.
 * @returns {{code:'vencido'|'urgente'|'proximo'|'sinFecha'|'normal', dias:number|null}}
 */
export function clasificarEndosoPendiente(pendiente, nowMs = Date.now()) {
  const limite = toMs(pendiente?.fechaLimite);
  if (!limite) return { code: 'sinFecha', dias: null };
  const dias = Math.ceil((limite - nowMs) / 86400000);
  if (dias < 0) return { code: 'vencido', dias };
  if (dias <= 7) return { code: 'urgente', dias };
  if (dias <= 30) return { code: 'proximo', dias };
  return { code: 'normal', dias };
}

// Fields that constitute a fianza "snapshot" — the values an endoso can change
// and that the fianza root projects for existing consumers.
const SNAPSHOT_FIELDS = [
  'numeroFianza',
  'montoAfianzado',
  'monto',
  'tipoFianza',
  'inicioVigenciaFianza',
  'finVigenciaFianza',
];

// Labels for cambios (diff) display, covering snapshot fields plus fiado /
// beneficiario (which live inside xmlFianza).
export const CAMBIO_LABELS = {
  numeroFianza: 'Número de fianza',
  montoAfianzado: 'Monto afianzado',
  monto: 'Prima (neta)',
  tipoFianza: 'Tipo de fianza',
  inicioVigenciaFianza: 'Inicio de vigencia',
  finVigenciaFianza: 'Fin de vigencia',
  fiado: 'Fiado',
  beneficiario: 'Beneficiario',
};

// Delegates to the canonical coercer so ISO strings and Firestore's {seconds}
// shape are understood too — handling only {toMillis} made those diff as changed.
function toMs(v) {
  return toTimestamp(v);
}

// Every tipo an evento represents. Older eventos carry only `tipo`; reading
// through here means the UI never has to know which vintage it is looking at.
export function getEventoTipos(evento) {
  if (Array.isArray(evento?.tipos) && evento.tipos.length) return evento.tipos;
  return evento?.tipo ? [evento.tipo] : [];
}

// es-MX label for an evento, joining a mixed endoso ("Aumento + Cambio de texto").
export function formatEventoTipos(evento) {
  const tipos = getEventoTipos(evento);
  if (!tipos.length) return '';
  return tipos.map((t) => EVENTO_TIPO_LABELS[t] || t).join(' + ');
}

// The fianza's lifecycle state. Absent field ⇒ activa (no migration needed).
export function getEstadoFianza(fianza) {
  return fianza?.estadoFianza || ESTADO_FIANZA.ACTIVA;
}

// Build a snapshot (current projected values) from a fianza-like object.
export function snapshotFromFianza(fianza) {
  const snap = {};
  SNAPSHOT_FIELDS.forEach((k) => { snap[k] = fianza?.[k] ?? null; });
  // `monto` in a snapshot is the prima NETA vigente, never the CFDI total: the
  // "después" side (montoProyectado) is built from net primas, and comisión is
  // earned on the neta. On an emisión-only fianza the root `monto` is the gross
  // factura total, so reading it here made an aumento compare gross→net and
  // render as a prima drop. Root `monto` stays as the last-resort fallback for
  // fianzas with no net reading at all (getPrimaAmount already ranks it there).
  const primaNeta = getPrimaAmount(fianza || {});
  if (primaNeta) snap.monto = primaNeta;
  snap.fiado = fianza?.xmlFianza?.fiado || null;
  snap.beneficiario = fianza?.xmlFianza?.beneficiario || null;
  return snap;
}

// The current valid version (projected root values). Today this is just the root
// fields, exposed as an accessor so consumers never reach into eventos[].
export function resolveActiveVersion(fianza) {
  return snapshotFromFianza(fianza || {});
}

// Build the implicit "emisión" event from a fianza's root fields. Used both to
// seed newly-created fianzas and to lazily materialize eventos[0] for legacy
// fianzas that predate this feature.
export function createEmisionEvento(fianza) {
  const createdAt = fianza?.createdAt || null;
  return {
    id: `evento_${createdAt || Date.now()}`,
    tipo: EVENTO_TIPOS.EMISION,
    fecha: toMs(fianza?.inicioVigenciaFianza) || createdAt || null,
    createdAt,
    createdBy: fianza?.createdBy || null,
    origen: 'fianza',
    contratoDocumentoId: null,
    filePaths: fianza?.filePaths || {},
    xmlFianza: fianza?.xmlFianza || null,
    xmlFactura: fianza?.xmlFactura || null,
    snapshot: snapshotFromFianza(fianza || {}),
    cambios: [],
    nota: null,
  };
}

// Return the fianza's eventos, materializing the implicit emisión as eventos[0]
// when the array is missing/empty (legacy fianzas). Never mutates the input.
export function ensureEventos(fianza) {
  if (Array.isArray(fianza?.eventos) && fianza.eventos.length > 0) {
    return fianza.eventos;
  }
  return [createEmisionEvento(fianza || {})];
}

// ── Factura ledger ─────────────────────────────────────────────────────────
// The fianza carries a `facturas[]` ledger. Legacy fianzas (single xmlFactura /
// monto, no array) read with ZERO migration: facturas[0] is synthesized from the
// root factura fields on the fly, exactly like ensureEventos does for eventos[].

// Build the implicit emisión factura from a fianza's root fields. `pagada` is
// read from the root (legacy per-fianza flag) so the synthesized entry inherits
// whatever collection state the fianza already recorded.
export function createEmisionFactura(fianza) {
  const createdAt = fianza?.createdAt || null;
  const ts = createdAt || Date.now();
  return {
    id: `factura_${ts}`,
    mode: FACTURA_MODES.EMISION,
    facturaId:
      fianza?.numeroFactura ||
      fianza?.xmlFactura?.noIdentificacion ||
      fianza?.xmlFactura?.folio ||
      null,
    prima: getPrimaAmount(fianza),
    monto: fianza?.monto ?? null,
    fecha:
      toMs(fianza?.xmlFactura?.fecha) ||
      toMs(fianza?.inicioVigenciaFianza) ||
      createdAt ||
      null,
    // Per-factura collection gate. `pagada` unlocks the comisión → cobrada
    // transition; it is never auto-flipped.
    pagada: fianza?.pagada ?? false,
    pagadaFecha: fianza?.pagadaFecha ?? null,
    filePaths: {
      facturaPdf: fianza?.filePaths?.facturaPdf || null,
      facturaXml: fianza?.filePaths?.facturaXml || null,
    },
    xmlFactura: fianza?.xmlFactura || null,
    supersedesFacturaId: null,
    eventoId: `evento_${ts}`,
  };
}

// Return the fianza's facturas, materializing the implicit emisión factura as
// facturas[0] when the array is missing/empty. Never mutates the input.
export function ensureFacturas(fianza) {
  if (Array.isArray(fianza?.facturas) && fianza.facturas.length > 0) {
    return fianza.facturas;
  }
  return [createEmisionFactura(fianza || {})];
}

// Factory for a new factura ledger entry (adicional / sustituye), stamped with
// its own id and fecha.
//
// `pagada` defaults to false — a freshly issued factura is not paid yet. The one
// exception is a SUSTITUYE against an already-paid factura: the afianzadora
// cancels and reissues, marks the new one paid on the spot, and keeps the
// difference as a saldo a favor del fiado (they rarely refund it, since the fiado
// will need another fianza). Callers pass `pagada`/`saldoAFavor` for that case —
// see buildFacturaSustituye.
export function buildFactura({
  mode,
  facturaId = null,
  prima = 0,
  monto = null,
  fecha = null,
  filePaths = {},
  xmlFactura = null,
  supersedesFacturaId = null,
  eventoId = null,
  pagada = false,
  pagadaFecha = null,
  saldoAFavor = 0,
  ts = null,
}) {
  const now = ts || Date.now();
  return {
    id: `factura_${now}`,
    mode,
    facturaId,
    prima: Number(prima) || 0,
    monto,
    fecha: fecha || now,
    pagada,
    pagadaFecha: pagada ? (pagadaFecha || fecha || now) : null,
    // Money the fiado already paid that the reissued factura no longer covers.
    // Sits as a credit with the afianzadora; a carta de crédito documents it.
    saldoAFavor: Number(saldoAFavor) || 0,
    filePaths,
    xmlFactura,
    supersedesFacturaId,
    eventoId,
  };
}

// A factura that REPLACES the current valid set (the afianzadora cancelled and
// reissued). Carries over the paid state of what it supersedes and computes the
// fiado's credit balance when the new prima is lower than what was already paid.
export function buildFacturaSustituye({ supersedes = [], prima, ...rest }) {
  const pagadas = supersedes.filter((f) => f?.pagada);
  const primaPagada = pagadas.reduce((s, f) => s + (Number(f.prima) || 0), 0);
  const nuevaPrima = Number(prima) || 0;
  // Paid only if the whole superseded set was paid — a partially paid base
  // leaves the reissued factura pending.
  const heredaPagada = supersedes.length > 0 && pagadas.length === supersedes.length;
  return buildFactura({
    ...rest,
    mode: FACTURA_MODES.SUSTITUYE,
    prima: nuevaPrima,
    pagada: heredaPagada,
    pagadaFecha: heredaPagada
      ? pagadas.reduce((latest, f) => Math.max(latest, Number(f.pagadaFecha) || 0), 0) || null
      : null,
    saldoAFavor: heredaPagada ? Math.max(0, primaPagada - nuevaPrima) : 0,
    supersedesFacturaId: supersedes.length ? supersedes[supersedes.length - 1].id : null,
  });
}

// The fiado's outstanding credit with the afianzadora across the whole ledger.
export function getSaldoAFavor(fianza) {
  return ensureFacturas(fianza).reduce((s, f) => s + (Number(f?.saldoAFavor) || 0), 0);
}

// Valid prima for the fianza today = sum of the vigente facturas' prima. This is
// the money base every later factura earns comisión on (prima × locked pct).
//
// Identical to getPrimaAmount by construction: that accessor folds facturas[]
// when present and otherwise synthesizes the emisión factura from the same root
// fields ensureFacturas would. Kept as a named alias because "prima vigente" is
// the domain term, and so the two can never drift apart again.
export function getValidPrima(fianza) {
  return getPrimaAmount(fianza);
}

// The projected root `monto` (the total prima billed) and the factura ledger
// entry's prima are NOT the same number for an ADICIONAL — conflating them is
// what made "Prima total" collapse to the differential invoice.
//
//   adicional → the afianzadora bills only the DIFFERENCE. The ledger entry
//               carries that difference and accrues on top of the current base;
//               the root total becomes prima actual + diferencia.
//   sustituye → they cancel and reissue. The factura IS the new total and
//               supersedes the prior set, so both numbers are that total.
//
// Keyed on `efectoPrima`, NOT on the endoso tipo: an ampliación de vigencia bills
// an adicional while montoAfianzado never moves, and an endoso the user labelled
// "cambio de texto" can still arrive with a factura.
//
// Manual mode has no factura to read: the user types the new cumulative NET
// prima ("prima neta después del endoso") and the difference is derived from it.
//
// Returns null when the prima cannot be determined, so the caller can block
// instead of guessing — the old fallback guessed the current prima and booked a
// factura (and a comisión) for the whole existing amount.
//
// `facturaPrima` is the factura's NET prima (Σ conceptos de prima), never its
// CFDI total: the total bundles derechos, gastos de expedición and IVA, and
// booking it here inflates both the fianza's prima vigente and every comisión
// derived from it. The parameter used to be called `facturaTotal`, and callers
// duly passed the total.
export function resolvePrimaEndoso({ efectoPrima, primaActual, facturaPrima, manualTotal }) {
  const actual = Number(primaActual) || 0;
  if (efectoPrima === EFECTO_PRIMA.ADICIONAL) {
    if (facturaPrima != null) {
      return { montoProyectado: actual + facturaPrima, facturaPrima };
    }
    if (manualTotal != null) {
      return { montoProyectado: manualTotal, facturaPrima: manualTotal - actual };
    }
    return null;
  }
  if (efectoPrima === EFECTO_PRIMA.SUSTITUYE) {
    const total = facturaPrima != null ? facturaPrima : manualTotal;
    if (total == null) return null;
    return { montoProyectado: total, facturaPrima: total };
  }
  return null;
}

// The comisión % locked at emisión. Persisted on the fianza root so a later
// factura is NOT re-tramoed (a $1 aumento would falsely read as prima mínima).
// Returns null for legacy fianzas that predate the lock — callers derive it on
// the fly from the tarifa tables in that case.
export function getComisionPct(fianza) {
  const pct = fianza?.comisionPct;
  return pct == null ? null : Number(pct);
}

// Format a snapshot/cambio value for display (es-MX). Currency for monto fields,
// dates for vigencia, name for fiado/beneficiario objects.
export function formatCambioValor(campo, valor) {
  if (valor == null || valor === '') return '—';
  if (campo === 'inicioVigenciaFianza' || campo === 'finVigenciaFianza') {
    const n = valor?.toMillis?.() ?? Number(valor);
    if (!n || Number.isNaN(n)) return '—';
    return new Date(n).toLocaleDateString('es-MX');
  }
  if (campo === 'montoAfianzado' || campo === 'monto') {
    const n = Number(valor);
    if (Number.isNaN(n)) return String(valor);
    return new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN' }).format(n);
  }
  if (campo === 'fiado' || campo === 'beneficiario') {
    if (valor && typeof valor === 'object') return valor.nombre || '—';
    return String(valor);
  }
  return String(valor);
}

// Order matters: numbers and numeric strings are resolved as NUMBERS first.
// Coercing through the date parser first would read a montoAfianzado of "2024"
// as a year and diff it against 2024 as a spurious change.
function normalizeForCompare(v) {
  if (v == null || v === '') return null;
  if (typeof v === 'number') return v;
  if (typeof v === 'object') {
    const ms = toMs(v);
    return ms != null ? ms : JSON.stringify(v);
  }
  // Montos reach us as both "500000" and 500000 depending on whether they came
  // from a form or from the XML; the same amount must not diff as a change.
  const n = Number(String(v).replace(/,/g, ''));
  return Number.isNaN(n) ? v : n;
}

// Diff two snapshots into [{ campo, label, valorAnterior, valorNuevo }] over the
// fields that actually changed.
export function computeCambios(before, after) {
  const cambios = [];
  Object.keys(CAMBIO_LABELS).forEach((campo) => {
    const a = normalizeForCompare(before?.[campo]);
    const b = normalizeForCompare(after?.[campo]);
    if (a !== b) {
      cambios.push({
        campo,
        label: CAMBIO_LABELS[campo],
        valorAnterior: before?.[campo] ?? null,
        valorNuevo: after?.[campo] ?? null,
      });
    }
  });
  return cambios;
}

// Infer the endoso tipo from a before→after snapshot diff. Used to auto-classify
// when the user uploads the endoso's fianza/factura XML. Priority: a monto change
// dominates (aumento/disminución), then vigencia (duration change → aumento/
// disminución de vigencia, pure date shift → cambio de vigencia), then fiado/
// beneficiario, then other data, else a documental change (cambio de texto).
// Numeric coercion for comparison. Same ordering rule as normalizeForCompare:
// plain numbers win, and only timestamp-shaped OBJECTS ({toMillis} / {seconds})
// go through the date coercer.
function _num(v) {
  if (v == null || v === '') return null;
  if (typeof v === 'number') return v;
  if (typeof v === 'object') return toMs(v);
  const n = Number(String(v).replace(/,/g, ''));
  return Number.isNaN(n) ? null : n;
}
function _nombre(x) {
  return x && typeof x === 'object' ? x.nombre || '' : x || '';
}
// How the vigencia window moved: 'igual' | 'amplia' | 'reduce' | 'desplaza'.
// Exported because the prima effect depends on the DIRECTION, not just the fact
// that the dates changed — extending a multi-year fianza bills more.
export function compareVigencia(before, after) {
  const bIni = _num(before?.inicioVigenciaFianza);
  const aIni = _num(after?.inicioVigenciaFianza);
  const bFin = _num(before?.finVigenciaFianza);
  const aFin = _num(after?.finVigenciaFianza);
  const changed = (aIni != null && aIni !== bIni) || (aFin != null && aFin !== bFin);
  if (!changed) return 'igual';
  const bDur = bFin != null && bIni != null ? bFin - bIni : null;
  const aDur = aFin != null && aIni != null ? aFin - aIni : null;
  if (bDur == null || aDur == null || aDur === bDur) return 'desplaza';
  return aDur > bDur ? 'amplia' : 'reduce';
}

// Every change this endoso represents. Plural on purpose: an endoso is routinely
// an aumento AND a cambio de texto, and reporting "how many vigencia endosos"
// must not lose the mixed ones.
export function inferEndosoTipos(before, after) {
  const tipos = [];

  const bMonto = _num(before?.montoAfianzado);
  const aMonto = _num(after?.montoAfianzado);
  if (bMonto != null && aMonto != null && aMonto !== bMonto) {
    tipos.push(aMonto > bMonto ? EVENTO_TIPOS.AUMENTO : EVENTO_TIPOS.DISMINUCION);
  }

  const vig = compareVigencia(before, after);
  if (vig === 'amplia' || vig === 'reduce') tipos.push(EVENTO_TIPOS.AUMENTO_DISMINUCION_VIGENCIA);
  else if (vig === 'desplaza') tipos.push(EVENTO_TIPOS.CAMBIO_VIGENCIA);

  if (_nombre(after?.fiado) !== _nombre(before?.fiado) ||
      _nombre(after?.beneficiario) !== _nombre(before?.beneficiario)) {
    tipos.push(EVENTO_TIPOS.CAMBIO_FIADO_BENEFICIARIO);
  }

  if ((after?.numeroFianza ?? null) !== (before?.numeroFianza ?? null) ||
      (after?.tipoFianza ?? null) !== (before?.tipoFianza ?? null)) {
    tipos.push(EVENTO_TIPOS.CAMBIO_DATOS);
  }

  // Nothing structural moved — whatever changed is wording.
  return tipos.length ? tipos : [EVENTO_TIPOS.CAMBIO_TEXTO];
}

// Back-compat single-value view; `tipo` remains the primary label on the evento.
export function inferEndosoTipo(before, after) {
  return inferEndosoTipos(before, after)[0];
}

// What the prima did, inferred from the DIRECTION of the change. Editable by the
// user in the dialog — the afianzadora does not always follow the default.
//
// `hasFactura` matters on its own: if the agente attached a factura XML, money
// moved by definition, so we never return NINGUNO and force a real choice.
export function inferEfectoPrima(before, after, { hasFactura = false } = {}) {
  const bMonto = _num(before?.montoAfianzado);
  const aMonto = _num(after?.montoAfianzado);
  if (bMonto != null && aMonto != null && aMonto !== bMonto) {
    return aMonto > bMonto ? EFECTO_PRIMA.ADICIONAL : EFECTO_PRIMA.SUSTITUYE;
  }

  // Monto unchanged but the window moved: on a multi-year fianza this is exactly
  // where the prima changes without montoAfianzado ever moving.
  const vig = compareVigencia(before, after);
  if (vig === 'amplia') return EFECTO_PRIMA.ADICIONAL;
  if (vig === 'reduce') return EFECTO_PRIMA.SUSTITUYE;

  // A factura with no directional signal (shifted window, cambio de texto that
  // was nonetheless re-billed): default to complementing, let the user correct it.
  return hasFactura ? EFECTO_PRIMA.ADICIONAL : EFECTO_PRIMA.NINGUNO;
}

// Factory for a new event (endoso or terminal). Callers supply the before/after
// snapshots, any uploaded file paths, and parsed XML; this stamps id/fecha and
// computes the cambios diff for endoso events.
export function buildEvento({
  tipo,
  // Every change this endoso represents (see EFECTO_PRIMA for why these are two
  // separate axes). Defaults to [tipo] so existing callers keep working.
  tipos = null,
  efectoPrima = EFECTO_PRIMA.NINGUNO,
  snapshotAntes = null,
  snapshotDespues = null,
  filePaths = {},
  xmlFianza = null,
  xmlFactura = null,
  origen = 'fianza',
  contratoDocumentoId = null,
  nota = null,
  createdBy = null,
  // Link to the factura ledger entry this event produced (adicional / sustituye).
  facturaId = null,
  // Anulación requires a justification (fianzas cannot be anuladas freely).
  motivoCodigo = null,
  justificacion = null,
  // Substitution links (sustitucion event on the old fianza / the new one).
  sustituidaPorFianzaId = null,
  sustituyeAFianzaId = null,
  // Renovación link (renovacion event on the old fianza → its successor).
  renovadaPorFianzaId = null,
  ts = null,
}) {
  const now = ts || Date.now();
  const isEndoso = ENDOSO_TIPOS.includes(tipo);
  // `tipo` stays the primary label (existing eventos, timeline headings, the
  // EVENTO_TIPO_LABELS map); `tipos` carries the full set so a mixed endoso is
  // not flattened to whichever change happened to sort first.
  const tiposFinal = (Array.isArray(tipos) && tipos.length ? tipos : [tipo]).filter(Boolean);
  return {
    id: `evento_${now}`,
    tipo: tipo || tiposFinal[0] || null,
    tipos: tiposFinal,
    // The money axis. Only this decides whether the ledger moves.
    efectoPrima: efectoPrima || EFECTO_PRIMA.NINGUNO,
    fecha: now,
    createdAt: now,
    createdBy,
    origen,
    contratoDocumentoId,
    filePaths,
    xmlFianza,
    xmlFactura,
    snapshot: isEndoso ? snapshotDespues : null,
    cambios: isEndoso ? computeCambios(snapshotAntes, snapshotDespues) : [],
    nota,
    facturaId,
    motivoCodigo,
    justificacion,
    sustituidaPorFianzaId,
    sustituyeAFianzaId,
    renovadaPorFianzaId,
  };
}

// Identity fields on xmlFianza that RESOLVE THE COMISIÓN: the tarifa table is
// selected by (emisor, subRamo, tipo), and reports slice by ramo/fiado/
// beneficiario. An endoso XML that omits any of them must not erase it.
const XML_IDENTITY_SCALARS = ['ramo', 'subRamo'];
const XML_IDENTITY_OBJECTS = ['emisor', 'fiado', 'beneficiario', 'agente'];

// Merge `incoming` over `current`, keeping `current`'s value wherever the
// incoming one is absent/blank. Shallow, one level deep.
function coalesce(incoming, current) {
  if (!incoming) return current ?? null;
  if (!current) return incoming;
  const out = { ...current };
  Object.keys(incoming).forEach((k) => {
    const v = incoming[k];
    if (v !== null && v !== undefined && v !== '') out[k] = v;
  });
  return out;
}

// Project an endoso onto the embedded xmlFianza. Many consumers read
// fianza.xmlFianza.* (FianzaView "Datos de Fianza", getSubRamo/getEmisorNombre,
// proveedor-mismatch), so projecting onto the root alone would leave them stale.
//
// Two paths:
//   - A freshly-parsed XML from the afianzadora is authoritative for everything
//     it carries, but is merged rather than swapped in wholesale: a partial
//     endoso XML that omits emisor/subRamo/fiado would otherwise silently drop
//     the inputs that determine this fianza's comisión.
//   - Manual mode (no XML) patches the existing payload from the snapshot.
//
// `primaVigente` comes from the factura ledger after the endoso, so prima and
// tarifa stay consistent with what getPrimaAmount reports. tarifa is recomputed
// through calculateTarifa so multi-year fianzas stay annualized.
function projectXmlFianza(currentXml, incomingXml, snap, primaVigente) {
  const base = currentXml || null;
  if (!base && !incomingXml) return null;

  let xml = incomingXml ? { ...base, ...incomingXml } : { ...base };

  if (incomingXml && base) {
    XML_IDENTITY_SCALARS.forEach((k) => {
      if (!incomingXml[k]) xml[k] = base[k] ?? null;
    });
    XML_IDENTITY_OBJECTS.forEach((k) => {
      xml[k] = coalesce(incomingXml[k], base[k]);
    });
  }

  if (snap) {
    xml = {
      ...xml,
      numeroFianza: snap.numeroFianza ?? xml.numeroFianza,
      montoAfianzado: snap.montoAfianzado ?? xml.montoAfianzado,
      tipo: snap.tipoFianza ?? xml.tipo,
      inicioVigenciaFianza: snap.inicioVigenciaFianza ?? xml.inicioVigenciaFianza,
      finVigenciaFianza: snap.finVigenciaFianza ?? xml.finVigenciaFianza,
      fiado: coalesce(snap.fiado, xml.fiado),
      beneficiario: coalesce(snap.beneficiario, xml.beneficiario),
    };
  }

  const prima = primaVigente != null ? primaVigente : xml.primas?.prima;
  if (prima != null) {
    // A money endoso makes `prima` the CUMULATIVE neta vigente, while the rest
    // of the breakdown (derechos, gastos, IVA, subTotal, primaTotal) — whether
    // merged in from the endoso XML or left over from emisión — still describes
    // one single factura. Mixing them rendered a desglose whose subtotal was
    // smaller than its prima. When they disagree, keep only the cumulative
    // prima; the per-factura desgloses live on facturas[].xmlFactura.
    const breakdownPrima = parseNumber(xml.primas?.prima);
    xml.primas = breakdownPrima != null && breakdownPrima !== prima
      ? { prima }
      : { ...(xml.primas || {}), prima };
  }
  xml.tarifa =
    calculateTarifa(prima, xml.montoAfianzado, {
      vigencia: {
        inicio: toMs(xml.inicioVigenciaFianza),
        fin: toMs(xml.finVigenciaFianza),
      },
    }) ?? xml.tarifa ?? null;

  return xml;
}

// Keep xmlFactura.total / primas.primaTotal aligned with the projected `monto`
// (the factura total) so factura-derived reads don't go stale on a manual endoso.
function patchXmlFactura(xml, snap) {
  if (!xml || snap?.monto == null) return xml || null;
  return {
    ...xml,
    total: snap.monto,
    primas: { ...(xml.primas || {}), primaTotal: snap.monto },
  };
}

// Apply an event to a fianza, returning a NEW fianza object:
//   - endoso events project event.snapshot onto the root fields AND the embedded
//     xmlFianza/xmlFactura payloads (so every read path sees the current version)
//   - terminal events set estadoFianza and leave root vigencia/monto untouched
// The event is always appended to eventos (materializing emisión first if the
// fianza is legacy and has none).
// `extras.factura` — a factura ledger entry (from buildFactura) to append for
// money endosos (aumento → adicional, disminución → sustituye).
// `extras.sustituidaPor` — on a sustitución, the id of the new fianza entity
// that replaces this one; stamped onto the old fianza as `sustituidaPor`.
export function applyEndoso(fianza, evento, extras = {}) {
  const { factura = null, sustituidaPor = null, renovadaPor = null } = extras;
  const eventos = [...ensureEventos(fianza)];
  const next = { ...fianza };

  // Append the factura BEFORE projecting: the xmlFianza projection prices itself
  // off the resulting ledger, so the ledger has to be current first. Legacy
  // fianzas get facturas[0] materialized here (via ensureFacturas).
  if (factura) {
    next.facturas = [...ensureFacturas(fianza), factura];
    // The root `pagada` flag is a projection of the ledger, so it has to follow.
    // Both directions matter: an adicional on a settled fianza reopens it (there
    // is now an unpaid factura), while a sustituye that inherited the paid state
    // keeps it settled. Same rule FianzaView applies from the payment card.
    const vigentes = getFacturasVigentes(next.facturas);
    next.pagada = vigentes.length > 0 && vigentes.every((f) => f.pagada);
  }

  const terminalEstado = TERMINAL_TIPOS[evento.tipo];
  if (terminalEstado) {
    next.estadoFianza = terminalEstado;
    if (evento.tipo === EVENTO_TIPOS.SUSTITUCION && sustituidaPor) {
      next.sustituidaPor = sustituidaPor;
    }
  } else if (evento.snapshot) {
    SNAPSHOT_FIELDS.forEach((k) => {
      if (evento.snapshot[k] !== undefined) next[k] = evento.snapshot[k];
    });
    // The prima to project. With a ledger, Σ vigentes is authoritative; without
    // one, the freshly projected root `monto` (the factura total) is — NOT the
    // old xmlFianza.primas.prima, which is exactly what used to go stale here.
    const primaVigente =
      Array.isArray(next.facturas) && next.facturas.length > 0
        ? getPrimaAmount(next)
        : parseNumber(next.monto);
    next.xmlFianza = projectXmlFianza(
      next.xmlFianza,
      evento.xmlFianza,
      evento.snapshot,
      primaVigente
    );
    next.xmlFactura = evento.xmlFactura
      ? evento.xmlFactura
      : patchXmlFactura(next.xmlFactura, evento.snapshot);
    if (!next.estadoFianza) next.estadoFianza = ESTADO_FIANZA.ACTIVA;
  }

  // Renovación is NOT terminal: estado stays as-is (the vigencia simply
  // lapses); only the link to the successor fianza is stamped.
  if (renovadaPor) {
    next.renovadaPor = renovadaPor;
  }

  eventos.push(evento);
  next.eventos = eventos;
  return next;
}
